<template>
  <v-container fluid class="pa-6">
    <v-row class="align-center mb-4">
      <v-col cols="auto">
        <h2 class="text-h5">Deploy</h2>
      </v-col>
      <v-col cols="auto">
        <v-btn icon :loading="loading" @click="refresh">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- ===== Run Deploy ===== -->
    <v-card class="mb-6">
      <v-card-title class="subtitle-1 font-weight-bold">Run Deploy</v-card-title>
      <v-divider />
      <v-card-text>
        <v-row dense>
          <v-col cols="2">
            <select-project
              v-model="runForm.entity_id"
              label="Project"
              dense
              outlined
            />
          </v-col>
          <v-col cols="2">
            <v-select
              v-model="runForm.environment"
              :items="environmentOptions"
              label="Environment"
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="3">
            <v-autocomplete
              v-model="runForm.type_ids"
              :items="contentTypes"
              item-text="name"
              item-value="type_id"
              label="Types (empty = all)"
              dense
              outlined
              hide-details
              multiple
              chips
              small-chips
              deletable-chips
              clearable
            />
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model="runForm.note"
              label="Note (optional)"
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="2" class="d-flex align-center">
            <div class="mr-4">
              <span class="text-caption grey--text">Changed:</span>
              <strong class="ml-1">{{ changedCount }}</strong>
            </div>
          </v-col>
        </v-row>
        <v-row dense class="mt-2">
          <v-col cols="12" class="d-flex justify-end align-center">
            <v-switch
              v-model="runForm.full"
              label="Full"
              dense
              hide-details
              class="mr-4 mt-0 pt-0"
            />
            <v-tooltip bottom :disabled="!hasActiveDeploy">
              <template #activator="{ on, attrs }">
                <div v-on="on" v-bind="attrs">
                  <v-btn
                    color="primary"
                    :loading="runLoading"
                    :disabled="hasActiveDeploy || (!runForm.full && changedCount === 0)"
                    @click="onDeploy"
                  >
                    <v-icon left>mdi-rocket-launch</v-icon>
                    {{ runForm.full ? 'Full Deploy' : 'Deploy' }}
                  </v-btn>
                </div>
              </template>
              <span>A deploy is already running</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- ===== Active deploys ===== -->
    <v-card v-if="activeDeploys.length" class="mb-6">
      <v-card-title class="subtitle-1 font-weight-bold">Active</v-card-title>
      <v-divider />
      <v-list dense>
        <v-list-item v-for="deploy in activeDeploys" :key="deploy.generation_id">
          <v-list-item-content class="d-flex align-center" style="flex-direction: row;">
            <span class="font-weight-medium mr-3">#{{ deploy.generation_id }}</span>
            <v-chip :color="envColor(deploy.environment)" x-small outlined class="mr-2">{{ deploy.environment }}</v-chip>
            <v-chip :color="statusColor(deploy.status)" x-small dark class="mr-3">{{ deploy.status }}</v-chip>
            <v-progress-linear
              :value="deploy.progress || 0"
              :striped="deploy.status === 'running'"
              :animated="deploy.status === 'running'"
              color="blue"
              height="18"
              class="mr-3"
              style="max-width: 300px; border-radius: 4px;"
            >
              <template #default>
                <span class="white--text caption">{{ deploy.progress || 0 }}%</span>
              </template>
            </v-progress-linear>
            <span class="text-caption grey--text">{{ elapsedTime(deploy) }}</span>
            <v-spacer />
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn icon x-small color="red" v-on="on" v-bind="attrs" @click="onCancelClick(deploy)">
                  <v-icon small>mdi-stop</v-icon>
                </v-btn>
              </template>
              <span>Cancel deploy</span>
            </v-tooltip>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- ===== History ===== -->
    <v-card>
      <v-card-title class="subtitle-1 font-weight-bold">History</v-card-title>
      <v-divider />
      <v-data-table
        :headers="historyHeaders"
        :items="historyDeploys"
        :loading="loading"
        hide-default-footer
        :items-per-page="-1"
        @click:row="onRowClick"
        class="history-table"
      >
        <template #item.generation_id="{ value }">
          <span class="font-weight-medium">#{{ value }}</span>
        </template>
        <template #item.environment="{ value }">
          <v-chip :color="envColor(value)" x-small outlined>{{ value }}</v-chip>
        </template>
        <template #item.status="{ value }">
          <v-chip :color="statusColor(value)" x-small dark>
            <v-icon v-if="value === 'failed'" x-small left>mdi-alert</v-icon>
            <v-icon v-else-if="value === 'cancelled'" x-small left>mdi-cancel</v-icon>
            {{ value }}
          </v-chip>
        </template>
        <template #item.finished_at="{ value }">
          {{ formatDate(value) }}
        </template>
        <template #item.note="{ item }">
          <div class="d-flex align-center">
            <span>{{ item.note || '' }}</span>
            <v-tooltip v-if="item.status === 'failed' && item.meta && item.meta.error" bottom max-width="400">
              <template #activator="{ on, attrs }">
                <v-icon small color="red" class="ml-2" v-on="on" v-bind="attrs">mdi-alert-circle</v-icon>
              </template>
              <span>{{ item.meta.error.message }}</span>
            </v-tooltip>
            <v-icon
              v-else-if="item.status === 'done' && hasMeta(item)"
              small color="blue" class="ml-2"
            >mdi-information</v-icon>
          </div>
        </template>
        <template #item.actions="{ item }">
          <v-btn
            v-if="item.status === 'done'"
            icon
            x-small
            color="primary"
            @click.stop="onApplyClick(item)"
          >
            <v-icon small>mdi-backup-restore</v-icon>
          </v-btn>
        </template>
      </v-data-table>
      <v-card-actions v-if="hasMoreHistory">
        <v-spacer />
        <v-btn text small :loading="loadingMore" @click="loadMoreHistory">Load more</v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>

    <!-- ===== Prod Confirm Dialog ===== -->
    <v-dialog v-model="showProdConfirm" max-width="420">
      <v-card>
        <v-card-title class="red--text">Deploy to PRODUCTION?</v-card-title>
        <v-card-text>
          This will deploy content to the production environment.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showProdConfirm = false">Cancel</v-btn>
          <v-btn color="red" dark @click="confirmDeploy">Deploy</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== Apply Dialog ===== -->
    <v-dialog v-model="showApplyDialog" max-width="480">
      <v-card>
        <v-card-title>Apply Deploy #{{ applyTarget && applyTarget.generation_id }}</v-card-title>
        <v-card-text>
          <p>Re-deploy the content snapshot from this deploy.</p>
          <v-select
            v-model="applyEnvironment"
            :items="environmentOptions"
            label="Target environment"
            dense
            outlined
            hide-details
          />
          <v-text-field
            v-model="applyNote"
            label="Note (optional)"
            dense
            outlined
            hide-details
            class="mt-3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showApplyDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="applyLoading" @click="confirmApply">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== Cancel Dialog ===== -->
    <v-dialog v-model="showCancelDialog" max-width="440">
      <v-card v-if="cancelTarget">
        <v-card-title>Cancel Deploy #{{ cancelTarget.generation_id }}?</v-card-title>
        <v-card-text>
          <p>The current deploy will be stopped and marked as <strong>cancelled</strong>. Partial content is not published.</p>
          <v-text-field
            v-model="cancelNote"
            label="Reason (optional)"
            dense
            outlined
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showCancelDialog = false">Back</v-btn>
          <v-btn color="red" dark :loading="cancelLoading" @click="confirmCancel">Cancel deploy</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== Detail Dialog ===== -->
    <v-dialog v-model="showDetailDialog" max-width="520">
      <v-card v-if="detailTarget">
        <v-card-title>
          Deploy #{{ detailTarget.generation_id }}
          <v-spacer />
          <v-btn icon @click="showDetailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-simple-table dense class="pa-2">
          <tbody>
            <tr><td class="font-weight-medium" width="160">Environment</td><td><v-chip :color="envColor(detailTarget.environment)" x-small outlined>{{ detailTarget.environment }}</v-chip></td></tr>
            <tr><td class="font-weight-medium">Status</td><td><v-chip :color="statusColor(detailTarget.status)" x-small dark>{{ detailTarget.status }}</v-chip></td></tr>
            <tr><td class="font-weight-medium">Note</td><td>{{ detailTarget.note || '—' }}</td></tr>
            <tr><td class="font-weight-medium">Items</td><td>{{ detailTarget.item_count || 0 }}</td></tr>
            <tr v-if="detailTarget.source_generation_id"><td class="font-weight-medium">Source</td><td>Applied from <a href="#" @click.prevent="openSource(detailTarget.source_generation_id)">#{{ detailTarget.source_generation_id }}</a></td></tr>
            <tr><td class="font-weight-medium">Started</td><td>{{ formatDate(detailTarget.started_at) }}</td></tr>
            <tr><td class="font-weight-medium">Finished</td><td>{{ formatDate(detailTarget.finished_at) }}</td></tr>
            <tr><td class="font-weight-medium">Duration</td><td>{{ duration(detailTarget) }}</td></tr>
          </tbody>
        </v-simple-table>
        <!-- Meta / Error -->
        <template v-if="hasMeta(detailTarget)">
          <v-divider />
          <v-card-text class="pt-3">
            <template v-if="detailTarget.meta.error">
              <div class="d-flex align-center mb-2">
                <v-icon small color="red" class="mr-2">mdi-alert-circle</v-icon>
                <span class="font-weight-medium red--text">{{ detailTarget.meta.error.message }}</span>
              </div>
              <div v-if="detailTarget.meta.error.stack" class="mb-2">
                <v-btn x-small text @click="toggleStack(detailTarget.generation_id)">
                  {{ showStacks[detailTarget.generation_id] ? 'Hide' : 'Show' }} stack trace
                </v-btn>
                <pre v-if="showStacks[detailTarget.generation_id]" class="meta-stack mt-1">{{ detailTarget.meta.error.stack }}</pre>
              </div>
              <v-btn x-small outlined @click="copyError(detailTarget.meta.error)">
                <v-icon x-small left>mdi-content-copy</v-icon>
                Copy error
              </v-btn>
            </template>
            <template v-if="otherMetaKeys(detailTarget).length">
              <v-divider v-if="detailTarget.meta.error" class="my-3" />
              <v-simple-table dense>
                <tbody>
                  <tr v-for="key in otherMetaKeys(detailTarget)" :key="key">
                    <td class="font-weight-medium" width="160">{{ key }}</td>
                    <td>
                      <pre v-if="typeof detailTarget.meta[key] === 'object'" class="meta-json">{{ JSON.stringify(detailTarget.meta[key], null, 2) }}</pre>
                      <span v-else>{{ detailTarget.meta[key] }}</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </template>
          </v-card-text>
        </template>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { API } from '@/utils/api'
import { ENVIRONMENT_OPTIONS } from '@/components/cms/constants'
import SelectProject from '@/components/core/SelectProject/SelectProject'

const ENV_COLORS = { dev: 'grey', stage: 'orange', prod: 'green' }
const STATUS_COLORS = { pending: 'grey', running: 'blue', done: 'green', failed: 'red', cancelled: 'orange' }
const ACTIVE_STATUSES = ['pending', 'running']
const TERMINAL_STATUSES = ['done', 'failed', 'cancelled']

export default {
  components: { SelectProject },
  inject: { socket: { default: null } },
  data() {
    return {
      loading: false,
      loadingMore: false,
      runLoading: false,
      applyLoading: false,
      cancelLoading: false,
      environmentOptions: ENVIRONMENT_OPTIONS,
      contentTypes: [],
      deploys: [],
      historyPage: 1,
      historyPerPage: 20,
      historyTotal: 0,
      changedCount: 0,
      pollingTimers: {},
      now: Date.now(),
      nowInterval: null,

      runForm: {
        entity_id: null,
        environment: 'dev',
        type_ids: [],
        note: '',
        full: false,
      },

      showProdConfirm: false,
      showApplyDialog: false,
      showDetailDialog: false,
      showCancelDialog: false,

      applyTarget: null,
      applyEnvironment: 'dev',
      applyNote: '',

      cancelTarget: null,
      cancelNote: '',

      detailTarget: null,

      showStacks: {},

      historyHeaders: [
        { text: 'ID', value: 'generation_id', width: 80, sortable: false },
        { text: 'Environment', value: 'environment', width: 120, sortable: false },
        { text: 'Status', value: 'status', width: 100, sortable: false },
        { text: 'Items', value: 'item_count', width: 80, sortable: false },
        { text: 'Finished', value: 'finished_at', sortable: false },
        { text: 'Note', value: 'note', sortable: false },
        { text: '', value: 'actions', width: 50, sortable: false },
      ],
    }
  },
  computed: {

    activeDeploys() {
      return this.deploys.filter(g => ACTIVE_STATUSES.includes(g.status))
    },
    historyDeploys() {
      return this.deploys.filter(g => TERMINAL_STATUSES.includes(g.status))
    },
    hasActiveDeploy() {
      return this.activeDeploys.length > 0
    },
    hasMoreHistory() {
      return this.historyDeploys.length < this.historyTotal
    },
  },
  watch: {
    'runForm.entity_id'(val) {
      if (!val) {
        return
      }
      this.loadTypes()
      this.loadDeploys()
      this.loadChangedCount()
    },
    'runForm.environment'() {
      this.loadChangedCount()
    },
    'runForm.type_ids'() {
      this.loadChangedCount()
    },
    socket(newSocket, oldSocket) {
      if (oldSocket && oldSocket.value) {
        this.leaveDeployRoom(oldSocket.value)
      }
      if (newSocket && newSocket.value) {
        this.joinDeployRoom(newSocket.value)
      }
    },
  },
  mounted() {
    this.nowInterval = setInterval(() => {
      this.now = Date.now()
    }, 1000)
    if (this.socket && this.socket.value) {
      this.joinDeployRoom(this.socket.value)
    }
  },
  beforeDestroy() {
    if (this.nowInterval) {
      clearInterval(this.nowInterval)
    }
    Object.values(this.pollingTimers).forEach(t => clearTimeout(t))
    if (this.socket && this.socket.value) {
      this.leaveDeployRoom(this.socket.value)
    }
  },
  methods: {
    // --- Socket ---
    joinDeployRoom(sock) {
      sock.emit('joinRoom', { eventName: 'cms_deploy_status' })
      sock.on('cms_deploy_status', this.onDeployStatus)
    },
    leaveDeployRoom(sock) {
      sock.emit('leaveRoom', { eventName: 'cms_deploy_status' })
      sock.off('cms_deploy_status', this.onDeployStatus)
    },
    onDeployStatus(raw) {
      let data
      try {
        data = typeof raw === 'string' ? JSON.parse(raw) : raw
      } catch (e) {
        return
      }
      const deploy = this.deploys.find(g => g.generation_id === data.generation_id)
      if (deploy) {
        deploy.status = data.status
        deploy.progress = data.progress || 0
        if (data.item_count) {
          deploy.item_count = data.item_count
        }
        if (data.meta) {
          deploy.meta = data.meta
        }
        if (TERMINAL_STATUSES.includes(data.status)) {
          deploy.finished_at = new Date().toISOString()
          this.stopPolling(deploy.generation_id)
          this.loadChangedCount()
        }
      }
    },

    // --- Polling fallback ---
    startPolling(deployId) {
      if (this.pollingTimers[deployId]) {
        return
      }
      const poll = () => {
        const deployObj = this.deploys.find(g => g.generation_id === deployId)
        API.call('/cms/generation/get.json', { generation_id: deployId, entity_id: deployObj ? deployObj.entity_id : this.runForm.entity_id }).then(r => {
          const item = r.data && r.data.item ? r.data.item : r.data
          if (item) {
            const deploy = this.deploys.find(g => g.generation_id === deployId)
            if (deploy) {
              deploy.status = item.status
              deploy.progress = item.progress || 0
              deploy.item_count = item.item_count || deploy.item_count
              deploy.finished_at = item.finished_at || deploy.finished_at
              if (item.meta) {
                deploy.meta = item.meta
              }
            }
            if (TERMINAL_STATUSES.includes(item.status)) {
              this.stopPolling(deployId)
              this.loadChangedCount()
              return
            }
          }
          this.pollingTimers[deployId] = setTimeout(poll, 3000)
        }).catch(() => {
          this.pollingTimers[deployId] = setTimeout(poll, 5000)
        })
      }
      this.pollingTimers[deployId] = setTimeout(poll, 3000)
    },
    stopPolling(deployId) {
      if (this.pollingTimers[deployId]) {
        clearTimeout(this.pollingTimers[deployId])
        delete this.pollingTimers[deployId]
      }
    },

    // --- Data loading ---
    refresh() {
      this.loadTypes()
      this.loadDeploys()
      this.loadChangedCount()
    },
    loadTypes() {
      API.call('/cms/type/list.json').then(r => {
        const all = (r.data && r.data.items) || r.data || []
        this.contentTypes = all.filter(t => t.kind !== 'fragment')
      }).catch(() => {
        this.contentTypes = []
      })
    },
    loadDeploys() {
      Object.values(this.pollingTimers).forEach(t => clearTimeout(t))
      this.pollingTimers = {}
      this.loading = true
      this.historyPage = 1
      API.call('/cms/generation/list.json', {
        entity_id: this.runForm.entity_id,
        page: 1,
        perpage: this.historyPerPage,
      }).then(r => {
        const data = r.data || {}
        this.deploys = (data.items || []).map(g => this.normalizeDeploy(g))
        this.historyTotal = data.pagination ? data.pagination.total : this.deploys.length
        this.activeDeploys.forEach(g => {
          if (!this.socket || !this.socket.value) {
            this.startPolling(g.generation_id)
          }
        })
      }).catch(() => {
        this.deploys = []
      }).finally(() => {
        this.loading = false
      })
    },
    loadMoreHistory() {
      this.loadingMore = true
      this.historyPage++
      API.call('/cms/generation/list.json', {
        entity_id: this.runForm.entity_id,
        page: this.historyPage,
        perpage: this.historyPerPage,
      }).then(r => {
        const data = r.data || {}
        const items = (data.items || []).map(g => this.normalizeDeploy(g))
        const existingIds = new Set(this.deploys.map(g => g.generation_id))
        for (const item of items) {
          if (!existingIds.has(item.generation_id)) {
            this.deploys.push(item)
          }
        }
        this.historyTotal = data.pagination ? data.pagination.total : this.deploys.length
      }).finally(() => {
        this.loadingMore = false
      })
    },
    loadChangedCount() {
      API.call('/cms/item/list.json', {
        entity_id: this.runForm.entity_id,
        status: 'published',
        is_changed: true,
        perpage: 1,
      }).then(r => {
        const data = r.data || {}
        this.changedCount = data.pagination ? data.pagination.total : (data.items ? data.items.length : 0)
      }).catch(() => {
        this.changedCount = 0
      })
    },
    normalizeDeploy(g) {
      return {
        generation_id: g.generation_id,
        entity_id: g.entity_id,
        user_id: g.user_id,
        status: g.status || 'pending',
        note: g.note || '',
        type_ids: g.type_ids || [],
        environment: g.environment || 'dev',
        item_count: g.item_count || 0,
        progress: g.progress || 0,
        started_at: g.started_at || g.created,
        finished_at: g.finished_at || null,
        created: g.created,
        source_generation_id: g.source_generation_id || null,
        meta: g.meta || null,
      }
    },

    // --- Actions ---
    onDeploy() {
      if (this.runForm.full) {
        this.$root.$confirm('Full deploy', `Deploy ALL records to "${this.runForm.environment}"? This creates a version for every item.`)
          .then(ok => {
            if (ok) {
              this.confirmDeploy()
            }
          })
        return
      }
      if (this.runForm.environment === 'prod') {
        this.showProdConfirm = true
        return
      }
      this.confirmDeploy()
    },
    confirmDeploy() {
      this.showProdConfirm = false
      this.runLoading = true
      API.call('/cms/generation/run.json', {
        entity_id: this.runForm.entity_id,
        environment: this.runForm.environment,
        type_ids: this.runForm.type_ids.length ? this.runForm.type_ids : undefined,
        note: this.runForm.note || undefined,
        full: this.runForm.full || undefined,
      }).then(r => {
        const data = r.data || {}
        const item = data.item || data
        if (item && item.generation_id) {
          this.deploys.unshift(this.normalizeDeploy(item))
          if (!this.socket || !this.socket.value) {
            this.startPolling(item.generation_id)
          }
        }
        this.$showSuccess('Deploy started')
        this.runForm.note = ''
        this.runForm.full = false
      }).catch(() => {
        this.$showError('Failed to start deploy')
      }).finally(() => {
        this.runLoading = false
      })
    },
    onApplyClick(deploy) {
      this.applyTarget = deploy
      this.applyEnvironment = deploy.environment
      this.applyNote = ''
      this.showApplyDialog = true
    },
    confirmApply() {
      this.applyLoading = true
      API.call('/cms/generation/apply.json', {
        generation_id: this.applyTarget.generation_id,
        entity_id: this.applyTarget.entity_id,
        environment: this.applyEnvironment,
        note: this.applyNote || undefined,
      }).then(r => {
        const data = r.data || {}
        const item = data.item || data
        if (item && item.generation_id) {
          this.deploys.unshift(this.normalizeDeploy(item))
          if (!this.socket || !this.socket.value) {
            this.startPolling(item.generation_id)
          }
        }
        this.$showSuccess('Apply started')
        this.showApplyDialog = false
      }).catch(() => {
        this.$showError('Failed to apply deploy')
      }).finally(() => {
        this.applyLoading = false
      })
    },
    onCancelClick(deploy) {
      this.cancelTarget = deploy
      this.cancelNote = ''
      this.showCancelDialog = true
    },
    confirmCancel() {
      this.cancelLoading = true
      const deployId = this.cancelTarget.generation_id
      API.call('/cms/generation/cancel.json', {
        generation_id: deployId,
        entity_id: this.cancelTarget.entity_id,
        note: this.cancelNote || undefined,
      }).then(r => {
        const data = r.data || {}
        const item = data.item || data
        const deploy = this.deploys.find(g => g.generation_id === deployId)
        if (deploy && item && item.status) {
          deploy.status = item.status
          deploy.finished_at = item.finished_at || new Date().toISOString()
          if (item.meta) {
            deploy.meta = item.meta
          }
        }
        this.stopPolling(deployId)
        this.loadChangedCount()
        this.$showSuccess('Deploy cancelled')
        this.showCancelDialog = false
      }).catch(() => {
        // Error text is shown by API.call global toast. The deploy may already be
        // terminal (GENERATION_NOT_CANCELLABLE); refetch the actual status to sync UI.
        this.refreshDeploy(deployId)
        this.showCancelDialog = false
      }).finally(() => {
        this.cancelLoading = false
      })
    },
    refreshDeploy(deployId) {
      const deployObj = this.deploys.find(g => g.generation_id === deployId)
      API.call('/cms/generation/get.json', { generation_id: deployId, entity_id: deployObj ? deployObj.entity_id : this.runForm.entity_id }).then(r => {
        const item = r.data && r.data.item ? r.data.item : r.data
        if (!item) {
          return
        }
        const deploy = this.deploys.find(g => g.generation_id === deployId)
        if (deploy) {
          deploy.status = item.status
          deploy.progress = item.progress || 0
          deploy.finished_at = item.finished_at || deploy.finished_at
          if (item.meta) {
            deploy.meta = item.meta
          }
        }
        if (!ACTIVE_STATUSES.includes(item.status)) {
          this.stopPolling(deployId)
        }
      }).catch(() => {})
    },
    onRowClick(deploy) {
      this.detailTarget = deploy
      this.showDetailDialog = true
    },
    openSource(sourceId) {
      const deploy = this.deploys.find(g => g.generation_id === sourceId)
      if (deploy) {
        this.detailTarget = deploy
      } else {
        API.call('/cms/generation/get.json', { generation_id: sourceId, entity_id: this.detailTarget ? this.detailTarget.entity_id : this.runForm.entity_id }).then(r => {
          const item = r.data && r.data.item ? r.data.item : r.data
          if (item) {
            this.detailTarget = this.normalizeDeploy(item)
          }
        })
      }
    },

    // --- Meta helpers ---
    hasMeta(deploy) {
      return deploy.meta && typeof deploy.meta === 'object' && Object.keys(deploy.meta).length > 0
    },
    otherMetaKeys(deploy) {
      if (!deploy.meta || typeof deploy.meta !== 'object') {
        return []
      }
      return Object.keys(deploy.meta).filter(k => k !== 'error')
    },
    toggleStack(deployId) {
      this.$set(this.showStacks, deployId, !this.showStacks[deployId])
    },
    copyError(error) {
      const text = error.message + (error.stack ? '\n\n' + error.stack : '')
      navigator.clipboard.writeText(text).then(() => {
        this.$showSuccess('Error copied to clipboard')
      })
    },

    // --- Helpers ---
    envColor(env) {
      return ENV_COLORS[env] || 'grey'
    },
    statusColor(status) {
      return STATUS_COLORS[status] || 'grey'
    },
    formatDate(dateStr) {
      if (!dateStr) {
        return '—'
      }
      const d = new Date(dateStr)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },
    elapsedTime(deploy) {
      if (!deploy.started_at) {
        return ''
      }
      const start = new Date(deploy.started_at).getTime()
      const diffSec = Math.floor((this.now - start) / 1000)
      if (diffSec < 0) {
        return '0:00'
      }
      const min = Math.floor(diffSec / 60)
      const sec = diffSec % 60
      return `${min}:${String(sec).padStart(2, '0')}`
    },
    duration(deploy) {
      if (!deploy.started_at || !deploy.finished_at) {
        return '—'
      }
      const diffSec = Math.floor((new Date(deploy.finished_at) - new Date(deploy.started_at)) / 1000)
      if (diffSec < 60) {
        return `${diffSec}s`
      }
      const min = Math.floor(diffSec / 60)
      const sec = diffSec % 60
      return `${min}m ${sec}s`
    },
  },
}
</script>

<style scoped>
.history-table >>> tbody tr {
  cursor: pointer;
}
.meta-stack {
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
  background: #f5f5f5;
  padding: 8px 12px;
  border-radius: 4px;
  max-height: 300px;
  overflow-y: auto;
}
.meta-json {
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
  margin: 0;
}
</style>